import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from "rxjs";
import { AuthApiModel } from "../../api/models/auth.api.model";
import { tap } from "rxjs/operators";
import { isPlatformBrowser } from "@angular/common";
import { AuthenticationRestService } from "../../api/services/authentication.rest.service";
import { StorageService } from "./storage.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { NativeSecureSessionService } from '../mobile/native-secure-session.service';

@Injectable({
  providedIn: 'root'
})
export class JwtService {

  public token: string;
  public refreshToken: string;
  private remember: boolean;
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly jwt: JwtHelperService,
    private readonly as: AuthenticationRestService,
    private readonly storage: StorageService,
    private readonly nativeSecureSession: NativeSecureSessionService,
  ) {
    this.getTokenFromStorage();
  }

  public refreshJwtToken(): Observable<AuthApiModel> {
    this.getTokenFromStorage();
    return this.as.postRefreshToken(this.refreshToken).pipe(tap((authApiModel: AuthApiModel) => {
      this.setSession(authApiModel, this.hasRemember())
    }));
  }

  public getTokenFromStorage(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.token = this.storage.getItem('token');
      this.refreshToken = this.storage.getItem('refresh_token');
    }
  }

  private setToken(response: AuthApiModel): this {
    const data = this.jwt.decodeToken(response.token);
    this.token = response.token;
    this.storage.setItem('token', this.token);
    this.storage.setItem('token_expiration', JSON.stringify({ exp: data.exp, iat: data.iat }));
    void this.nativeSecureSession.write({
      token: this.token,
      tokenExpiration: JSON.stringify({ exp: data.exp, iat: data.iat }),
    });
    return this;
  }

  public setSession(response: AuthApiModel, remember: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setToken(response);
      if (remember) {
        this.setRemember(response);
      }
    }
  }

  public setRemember(response: AuthApiModel): this {
    if (response.refreshToken) {
      this.refreshToken = response.refreshToken;
      this.storage.setItem('refresh_token', this.refreshToken);
      void this.nativeSecureSession.write({refreshToken: this.refreshToken});
    }
    this.remember = true;
    this.storage.setItem('remember', 'true');
    void this.nativeSecureSession.write({remember: 'true'});
    return this;
  }

  public clearRemember(): this {
    this.refreshToken = null;
    this.remember = false;
    this.storage.setItem('refresh_token', this.refreshToken);
    this.storage.setItem('remember', 'false');
    void this.nativeSecureSession.remove(['refreshToken']);
    void this.nativeSecureSession.write({remember: 'false'});
    return this;
  }

  public hasRemember() {
    let ls: String = '';
    if (isPlatformBrowser(this.platformId)) {
      ls = this.storage.getItem('remember');
    }
    return ls === 'true';
  }

  public clearAllTokenRelatedFromStorage(): void {
    // clear token remove user from to log user out
    if (isPlatformBrowser(this.platformId)) {
      this.storage.removeItem('token');
      this.storage.removeItem('token_expiration');
      this.storage.removeItem('refresh_token');
      this.storage.removeItem('remember');
      void this.nativeSecureSession.clear();
    }
  }

  public decodeToken(token: string) {
    return this.jwt.decodeToken(token);
  }

  public isTokenExpired(): boolean | Promise<boolean> {
    this.getTokenFromStorage()
    return this.jwt.isTokenExpired(this.token);
  }
}
